import { useCallback, useEffect, useState, useRef } from "react";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import {
  ReportExtractionJobResponse,
  ReportExtractionJobResponseType,
} from "../models/reportExtraction";
import { z } from "zod";

interface UseReportExtractionsProps {
  fetchOnLoad?: boolean;
  limit?: number;
}

export const useReportExtractions = ({
  fetchOnLoad = false,
  limit = 20,
}: UseReportExtractionsProps) => {
  const [jobs, setJobs] = useState<ReportExtractionJobResponseType[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const hasFetched = useRef(false);

  const fetchJobs = useCallback(async () => {
    if (offset === 0) {
      setLoading(true);
    }
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: "/report-extractions",
        query: {
          limit: limit.toString(),
          offset: offset.toString(),
        },
      });
      // Handle both direct array response and wrapped response
      const jobsArray = Array.isArray(response) ? response : response.jobs;
      const data = z.array(ReportExtractionJobResponse).parse(jobsArray);
      setJobs((prev) => [...prev, ...data]);
      setOffset((prevOffset) => prevOffset + limit);
      if (data.length < limit) {
        setHasMore(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching report extraction jobs");
    } finally {
      setLoading(false);
    }
  }, [offset, limit]);

  const fetchJob = useCallback(
    async (jobId: string) => {
      try {
        const response = await fetchFastApi({
          method: "GET",
          path: `/report-extractions/${jobId}`,
        });
        const data = ReportExtractionJobResponse.parse(response);

        setJobs((prev) =>
          prev.map((job) => (job.jobId === jobId ? data : job)),
        );
      } catch (error) {
        console.error(error);
        toast.error("Error fetching report extraction job");
      }
    },
    [setJobs],
  );

  useEffect(() => {
    if (fetchOnLoad && !hasFetched.current) {
      fetchJobs();
      hasFetched.current = true;
    }
  }, [fetchOnLoad, fetchJobs]);

  return { jobs, loading, hasMore, fetchJobs, fetchJob };
};
